import type { InferenceStore } from 'store/inference/InferenceStore'
import type { ItemStore } from 'store/item/ItemStore'
import { GenerateRequest, ItemData } from 'type/Inference'
import type { Photo } from 'type/Options'
import { OfferItem } from 'type/rec'
import { mix } from 'util/array'

export type GenerateParams = {
  single?: ItemStore
  rec?: OfferItem
  source?: InferenceStore
}

export type CurrentItems = {
  item?: ItemStore
  comboItem?: ItemStore
}

function getItemImages(item: ItemStore) {
  const { selected_item_images, item_full_image, item_combo_image } = item.json
  if (selected_item_images?.length) return selected_item_images
  if (item_full_image) return [item_full_image]
  if (item_combo_image) return [item_combo_image]
  return []
}

function getInferenceImages(inference: InferenceStore) {
  const { selected_item_images } = inference
  if (selected_item_images?.length) return selected_item_images
  const { item_full_image, item_combo_image } = inference.items[0] ?? {}
  if (item_full_image) return [item_full_image]
  if (item_combo_image) return [item_combo_image]
  return []
}

function getRecImages(rec: OfferItem) {
  const { selected_item_images, item_full_image, item_combo_image } = rec
  if (selected_item_images?.length) return selected_item_images
  if (item_full_image) return [item_full_image]
  if (item_combo_image) return [item_combo_image]
  return []
}

function getBaseSelectedImages(params: GenerateParams, current: CurrentItems): string[] {
  const { single, source } = params
  if (single) return getItemImages(single)
  if (source) return getInferenceImages(source)
  if (current.item) return getItemImages(current.item)
  return []
}

function getComboSelectedImages(params: GenerateParams, current: CurrentItems): string[] {
  const { single, rec } = params
  if (single) return []
  if (rec) return getRecImages(rec)
  if (current.comboItem) return getItemImages(current.comboItem)
  return []
}

export function getSelectedImages(params: GenerateParams, current: CurrentItems): string[] {
  const base = getBaseSelectedImages(params, current)
  const combo = getComboSelectedImages(params, current)
  return mix(base, combo)
}

export function needCombineFull(combo_item_id: string | undefined, combine_full: boolean, type: Photo): boolean {
  if (combo_item_id) return false
  return combine_full && ['full', 'simple'].includes(type)
}

export type GenerateRequestInput = {
  user_id: string
  manager_id: string | undefined
  user_email: string
  user_ext_id: string | undefined
  identity_id: string
  vendor: string | undefined
  gemini_model: string | undefined
  high: boolean
  item: ItemData | undefined
  open_item_id: string | undefined
  combo_item_id: string | undefined
  combine_full: boolean
  type: Photo
}

export function buildGenerateRequest(input: GenerateRequestInput): GenerateRequest {
  const { user_id, manager_id, user_email, user_ext_id, identity_id, vendor, gemini_model, high, item,
    open_item_id, combo_item_id } = input
  const combo_generation = needCombineFull(combo_item_id, input.combine_full, input.type) ? true : undefined

  const item_ext_id = item?.external_item_id
  const ext = !!item_ext_id && item_ext_id === open_item_id
  const item_id = ext ? item_ext_id : item?.item

  if (!identity_id) throw new Error('no identity')
  if (!item_id) throw new Error('no item')

  return {
    user_id,
    manager_id,
    item_id,
    combo_item_id,
    identity_id,
    country_code: 'RU',
    user_email,
    user_ext_id,
    item_ext_id,
    high,
    vendor,
    gemini_model,
    combo_generation,
  }
}
